import React, { useMemo, useRef, useState } from 'react';
import './App.scss';
import { config as i18nConfig } from './i18n';
import { useTranslation } from 'react-i18next';
import MutationsOverview from './MutationsOverview';
import MutationsWithInputForNoun from './MutationsWithInputForNoun';
import ExplanationSoftMutation from './ExplanationSoftMutation';
import ExplanationHardMutation from './ExplanationHardMutation';
import ExplanationMixedMutation from './ExplanationMixedMutation';
import ExplanationSpiranteMutation from './ExplanationSpiranteMutation';
import MutationsTables from './MutationsTables';
import MutationsWithInputForVerb from './MutationsWithInputForVerb';
import { newNoun, newVerb } from './wordUtils';
import {
  lettersForMutations_idx,
  mutationTypes,
  possessive_headers,
  table_da_possessive_headers,
  table_en_possessive_headers,
  table_number_headers,
  table_number_headers_idx,
  table_possessive_headers,
} from './mutationUtils';
import Grid from '@mui/material/Grid';
import CssBaseline from '@mui/material/CssBaseline';
import useMediaQuery from '@mui/material/useMediaQuery';
import { createTheme, StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import ModalExplanationMutation from './ModalExplanationMutation';
import CardExplanationMutation from './CardExplanationMutation';
import AppFooter from './AppFooter';
import BtnSongTriFemoch from './BtnSongTriFemoch';
import BtnSongMaligorn from './BtnSongMaligorn';
import BtnSongTriMartolod from './BtnSongTriMartolod';
import BtnSongYannGorrig from './BtnSongYannGorrig';
import BtnQuote from './BtnQuote';
import ExplanationNoun_MS_FP_NounMutation from './ExplanationNoun_MS_FP_NounMutation';
import ExplanationNoun_FS_MP_NounMutation from './ExplanationNoun_FS_MP_NounMutation';
import CardForSongs from './CardForSongs';
import ExplanationAdjectiveMutation from './ExplanationAdjectiveMutation';
import Emm from './Emm';

const ctxModals = {
  modals: {
    spirante: null,
    hard: null,
    mixed: null,
    soft: null,
    incomplete: null,
    soft_incomplete: null,
    soft_conditional: null,
  },
};

export const ModalsContext = React.createContext(ctxModals.modals);

export const helpModes = {
  MODAL: 'MODAL',
  POPOVER: 'POPOVER',
  DRAWER: 'DRAWER',
};

const ctxHelpMode = {
  helpMode: helpModes.DRAWER,
};

export const HelpModeContext = React.createContext(null);

const ctxLanguage = {
  language: i18nConfig.lng,
};

export const LanguageContext = React.createContext(null);

export const themeModes = {
  dark: 'dark',
  light: 'light',
};

const getThemeLight = () => createTheme({
  palette: {
    mode: themeModes.light,
  },
});

const getThemeDark = () => createTheme({
  palette: {
    mode: themeModes.dark,
  },
});

function App () {

  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  console.log('*** prefersDarkMode', prefersDarkMode);

  const [ theme, setTheme ] = useState(getThemeLight);

  const setThemeAll = (newTheme) => {

    const docEl = document.documentElement;
    console.log('*** newTheme ', newTheme.palette.mode);

    if (newTheme.palette.mode === themeModes.dark) {

      docEl.style.setProperty('--lighten-pct', '-20%');
      docEl.style.setProperty('--darken-pct', '20%');
      document.body.classList.add('the-dark-mode');

    } else {

      docEl.style.setProperty('--lighten-pct', '20%');
      docEl.style.setProperty('--darken-pct', '-20%');
      document.body.classList.remove('the-dark-mode');
    }

    setTheme(newTheme);
  };

  useMemo(
    () => {
      if (prefersDarkMode) {
        setThemeAll(getThemeDark());
      } else {
        setThemeAll(getThemeLight());
      }
    },
    [ prefersDarkMode ],
  );


  const switchTheme = () => {
    if (theme.palette.mode === themeModes.light) {
      setThemeAll(getThemeDark());
    } else {
      setThemeAll(getThemeLight());
    }
  };

  const [ helpMode, setHelpMode ] = useState(ctxHelpMode.helpMode);
  const [ language, setLanguage ] = useState(ctxLanguage.language);

  ctxModals.modals.spirante = useRef(null);
  ctxModals.modals.hard = useRef(null);
  ctxModals.modals.mixed = useRef(null);
  ctxModals.modals.soft = useRef(null);
  ctxModals.modals.incomplete = useRef(null);
  ctxModals.modals.soft_incomplete = useRef(null);
  ctxModals.modals.soft_conditional = useRef(null);

  const { t, i18n } = useTranslation();

  function updateLanguage (selectedLanguage) {
    setLanguage(selectedLanguage);
    i18n.changeLanguage(selectedLanguage).then(() => {console.log('Language changed');});
  }

  const default_nouns = {
    'b': newNoun('bugel').people(),
    'd': newNoun('dousig').feminine().people(),
    'g': newNoun('godell').feminine(),
    'gw': newNoun('gwele'),
    'k': newNoun('ki'),
    'm': newNoun('mamm').feminine().people(),
    'p': newNoun('penn'),
    't': newNoun('tad').people(),
  };

  const row_groups_for_nouns = [ table_possessive_headers, table_en_possessive_headers, table_da_possessive_headers, table_number_headers ];

  const songs_for_nouns = {
    [ table_number_headers_idx[ 'tri/teir' ].label ]: {
      [ lettersForMutations_idx.p ]: () => (<BtnSongTriFemoch />),
      [ lettersForMutations_idx.m ]: () => (<BtnSongTriMartolod />),
    },
    [ table_number_headers_idx[ 'daou/div' ].label ]: { [ lettersForMutations_idx.m ]: () => (<BtnSongMaligorn />) },
    [ possessive_headers.e.label ]: {
      [ lettersForMutations_idx.p ]: () => (<BtnSongYannGorrig />),
      [ lettersForMutations_idx.t ]: () => (<BtnSongYannGorrig />),
      [ lettersForMutations_idx.b ]: () => (<BtnSongYannGorrig />),
      [ lettersForMutations_idx.d ]: () => (<BtnSongYannGorrig />),
    },
    [ possessive_headers.o.label ]: {
      [ lettersForMutations_idx.t ]: () => (<BtnSongTriFemoch />),
    },
    [ possessive_headers.da.label ]: {
      [ lettersForMutations_idx.t ]: () => (<BtnQuote>Luke,<br />Me eo <Emm mt={mutationTypes.SOFT}>da dad</Emm>.</BtnQuote>),
    },
  };

  const default_verbs = {
    'b': newVerb('bezañ'),
    'd': newVerb('debriñ'),
    'g': newVerb('gallout'),
    'gw': newVerb('gwiskañ'),
    'k': newVerb('kavout'),
    'm': newVerb('mont'),
    'p': newVerb('plijout'),
    't': newVerb('tremen'),
  };

  const verb_part_headers = {
    'e': { label: 'e', mutationType: mutationTypes.MIXED },
    'o': { label: 'o', mutationType: mutationTypes.MIXED },
    'ma': { label: 'ma', mutationType: mutationTypes.MIXED },
    'a': { label: 'a', mutationType: mutationTypes.SOFT },
    'da': { label: 'da', mutationType: mutationTypes.SOFT },
    'pa': { label: 'pa', mutationType: mutationTypes.SOFT },
    'ra': { label: 'ra', mutationType: mutationTypes.SOFT },
    'na': { label: 'na', mutationType: mutationTypes.SOFT },
    'ne': { label: 'ne', mutationType: mutationTypes.SOFT },
    'dre': { label: 'dre', mutationType: mutationTypes.SOFT },
    'en em': { label: 'en em', mutationType: mutationTypes.SOFT },
    'en ur': { label: 'en ur', mutationType: mutationTypes.SOFT },
    // TODO 'ad-' gbm
  };

  const table_verb_particules = Object.values(verb_part_headers);
  const row_groups_for_verbs = [ table_verb_particules ];

  const songs_for_verbs = {
    [ verb_part_headers.o.label ]: {
      [ lettersForMutations_idx.d ]: () => (<BtnQuote><Emm mt={mutationTypes.MIXED}>O tont</Emm> bremaik.</BtnQuote>),
    },
  };

  // const [ state, setState ] = React.useState({
  //   checkedA: true,
  //   checkedB: true,
  // });
  // const handleChange = (event) => {
  //   setState({ ...state, [ event.target.name ]: event.target.checked });
  // };

  return <>
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />

        <HelpModeContext.Provider value={[ helpMode, setHelpMode ]}>
          <LanguageContext.Provider value={[ language, updateLanguage ]}>
            <ModalsContext.Provider value={ctxModals.modals}>
              <main className="App">
                <ModalExplanationMutation ref={ctxModals.modals.spirante} mutationType={mutationTypes.SPIRANTE}>
                  <ExplanationSpiranteMutation />
                </ModalExplanationMutation>

                <ModalExplanationMutation ref={ctxModals.modals.hard} mutationType={mutationTypes.HARD}>
                  <ExplanationHardMutation />
                </ModalExplanationMutation>

                <ModalExplanationMutation ref={ctxModals.modals.mixed} mutationType={mutationTypes.MIXED} maxWidth={'lg'}>
                  <ExplanationMixedMutation />
                </ModalExplanationMutation>

                <ModalExplanationMutation ref={ctxModals.modals.soft} mutationType={mutationTypes.SOFT} maxWidth={'lg'}>
                  <ExplanationSoftMutation />
                </ModalExplanationMutation>

                <ModalExplanationMutation ref={ctxModals.modals.incomplete} mutationType={mutationTypes.INCOMPLETE}>
                  <ExplanationNoun_MS_FP_NounMutation />
                </ModalExplanationMutation>

                <ModalExplanationMutation ref={ctxModals.modals.soft_incomplete} mutationType={mutationTypes.SOFT_INCOMPLETE}>
                  <ExplanationNoun_FS_MP_NounMutation />
                </ModalExplanationMutation>

                <ModalExplanationMutation ref={ctxModals.modals.soft_conditional} mutationType={mutationTypes.SOFT_CONDITIONAL}>
                  <ExplanationAdjectiveMutation />
                </ModalExplanationMutation>

                <h1 className="visually-hidden">{t('home_welcome')}</h1>
                {/*<section className="section-mutations">*/}
                {/*  <h2 className="visually-hidden">{t('home_title_mutations')}</h2>*/}
                {/*  <ul className="sentences">*/}
                {/*    <li><AppMutations /></li>*/}
                {/*  </ul>*/}
                {/*</section>*/}
                <Grid container spacing={3}>

                  <Grid item xs={12} sm={8}>
                    <MutationsWithInputForNoun />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <MutationsWithInputForVerb />
                  </Grid>

                  <Grid item xs={12}>
                    <MutationsOverview />
                  </Grid>
                  <Grid item xs={12}>
                    <MutationsTables title={'Overview for nouns'} words={default_nouns} rowGroups={row_groups_for_nouns} mutationsToShow={[ mutationTypes.SPIRANTE, mutationTypes.HARD, mutationTypes.SOFT ]}
                                     songs={songs_for_nouns} />
                  </Grid>
                  <Grid item xs={12}>
                    <MutationsTables title={'Overview for verbs'} words={default_verbs} rowGroups={row_groups_for_verbs} mutationsToShow={[ mutationTypes.MIXED, mutationTypes.SOFT ]}
                                     songs={songs_for_verbs} />
                  </Grid>
                  <Grid item xs={12}>
                    <CardForSongs />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <CardExplanationMutation mutationType={mutationTypes.SPIRANTE}>
                      <ExplanationSpiranteMutation />
                    </CardExplanationMutation>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <CardExplanationMutation mutationType={mutationTypes.HARD}>
                      <ExplanationHardMutation />
                    </CardExplanationMutation>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <CardExplanationMutation mutationType={mutationTypes.MIXED}>
                      <ExplanationMixedMutation />
                    </CardExplanationMutation>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <CardExplanationMutation mutationType={mutationTypes.SOFT}>
                      <ExplanationSoftMutation />
                    </CardExplanationMutation>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <CardExplanationMutation mutationType={mutationTypes.INCOMPLETE}>
                      <ExplanationNoun_MS_FP_NounMutation />
                    </CardExplanationMutation>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <CardExplanationMutation mutationType={mutationTypes.SOFT_INCOMPLETE}>
                      <ExplanationNoun_FS_MP_NounMutation />
                    </CardExplanationMutation>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <CardExplanationMutation mutationType={mutationTypes.SOFT_CONDITIONAL}>
                      <ExplanationAdjectiveMutation />
                    </CardExplanationMutation>
                  </Grid>

                  <Grid item xs={12}>
                    <AppFooter theme={theme} switchTheme={switchTheme} />
                  </Grid>
                </Grid>
                {/*<section className={'articles'}></section>*/}

              </main>
            </ModalsContext.Provider>
          </LanguageContext.Provider>
        </HelpModeContext.Provider>
      </ThemeProvider>
    </StyledEngineProvider>
  </>;
}

export default App;
